import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../pictures/home/logo-m.png";
import floral from "../pictures/home/floral.png";
import "./home/styles/base.css";
import "./home/styles/rtl.css";
import { RichText, useSiteTranslation } from "./home/i18n";
import { useLanguageSwitch } from "./home/hooks/useLanguageSwitch";
import "./login.css";

export default function LoginComponent() {
  const navigate = useNavigate();
  const { currentUser, login } = useAuth();
  const { t, language, dir } = useSiteTranslation();
  const { setLanguage } = useLanguageSwitch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Someone who is already signed in has no reason to see the form.
  useEffect(() => {
    if (currentUser) navigate("/dashboard", { replace: true });
  }, [currentUser, navigate]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t("login.title");
    document.documentElement.lang = language;
    return () => {
      document.title = previousTitle;
      document.documentElement.lang = "en";
    };
  }, [t, language]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    login(username.trim(), password)
      .then((user) => {
        if (user) navigate("/dashboard");
        else setError(t("login.unauthorized"));
      })
      .catch((err) =>
        setError(
          err.response
            ? t("login.invalid")
            : t("login.offline")
        )
      )
      .finally(() => setLoading(false));
  };

  return (
    <div className="mjm-home login-page" dir={dir} lang={language}>
      <aside className="login-art">
        <Link to="/" className="login-brand" aria-label={t("common.brandHome")}>
          <img src={logo} alt="MJMarry" />
        </Link>
        <div className="login-art-copy">
          <p className="eyebrow">{t("login.artEyebrow")}</p>
          <h2>
            <RichText k="login.artTitle" />
          </h2>
          <p className="login-script">{t("home.hero.script")}</p>
        </div>
        <p className="login-art-foot">{t("common.rights", { year: new Date().getFullYear() })}</p>
        <img className="login-floral" src={floral} alt="" aria-hidden="true" />
      </aside>

      <main className="login-main">
        <Link to="/" className="login-back">
          <span aria-hidden="true">←</span> {t("login.back")}
        </Link>
        <button
          type="button"
          className="login-lang"
          lang={language === "ar" ? "en" : "ar"}
          onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
        >
          {language === "ar" ? "English" : "العربية"}
        </button>

        <form className="login-card" onSubmit={handleSubmit}>
          <img className="login-card-logo" src={logo} alt="" aria-hidden="true" />
          <p className="eyebrow">{t("login.eyebrow")}</p>
          <h1>{t("login.heading")}</h1>
          <p className="login-lead">{t("login.lead")}</p>

          <label className="login-field">
            <span>{t("login.username")}</span>
            <input
              type="text"
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck="false"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              required
            />
          </label>

          <label className="login-field">
            <span>{t("login.password")}</span>
            <div className="login-password">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((shown) => !shown)}
                aria-label={showPassword ? t("login.hideLabel") : t("login.showLabel")}
                aria-pressed={showPassword}
                disabled={loading}
              >
                {showPassword ? t("login.hide") : t("login.show")}
              </button>
            </div>
          </label>

          {error && (
            <p className="login-error" role="alert">
              {error}
            </p>
          )}

          <button className="login-submit" type="submit" disabled={loading}>
            {loading ? <span className="login-spinner" aria-label={t("login.signingIn")} /> : t("login.submit")}
          </button>

          <p className="login-help">
            {t("login.help")} <Link to="/help">{t("login.helpLink")}</Link>
          </p>
        </form>
      </main>
    </div>
  );
}
